import { format, isToday } from "date-fns";
import { useLocale, useTranslations } from "next-intl";
import Star from "@/assets/icons/star.svg";
import { getDateLocale } from "@/features/cart/utils/dates";
import { RATING_CATEGORY_FIELDS } from "@/features/rating/helpers/rating-category";
import type { ProductReviewItem } from "@/features/rating/types/product-rating.type";
import { cn } from "@/lib/utils";
import { DeliveryReview } from "./delivery-review";
import { StarRating } from "./star-rating";

export const ReviewItem = ({
  review,
  merchantName,
}: {
  review: ProductReviewItem;
  merchantName?: string;
}) => {
  const t = useTranslations("rating");
  const tCategories = useTranslations("rating.rateDialog.categories");
  const locale = useLocale();
  const dateLocale = getDateLocale(locale);

  const userName = review.userName ?? t("textReview.anonymous");
  const formattedDate = format(new Date(review.createdAt), "EEEE dd.MM.yyyy", {
    locale: dateLocale,
  });
  const capitalizedDate =
    formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  const answerDate = (() => {
    if (!review.merchantAnswerAt) {
      return null;
    }
    const date = new Date(review.merchantAnswerAt);
    if (isToday(date)) {
      return t("merchantAnswer.today");
    }
    const formatted = format(date, "EEEE dd.MM.yyyy", { locale: dateLocale });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  })();

  return (
    <div
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-lg bg-brand-grey1 pt-4 pb-4",
        (review.merchantAnswer || review.deliveryRating) && "pb-0",
      )}
    >
      <div className="flex items-start justify-between gap-x-4 px-4 md:px-6">
        <div className="flex flex-col">
          <span className="font-medium text-sm">{userName}</span>
          <span className="text-muted-foreground text-xs">
            {capitalizedDate}
          </span>
        </div>
        <div className="flex items-center gap-x-2">
          <StarRating
            rating={review.rating}
            starClassName="h-4 w-4"
            containerClassName="gap-1"
          />
          <span className="font-medium">{review.rating.toFixed(1)}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-flow-col grid-cols-2 grid-rows-2 gap-x-4 gap-y-2 px-4 md:mt-2 md:flex md:flex-wrap md:gap-x-6 md:gap-y-1.5 md:px-6">
        {RATING_CATEGORY_FIELDS.map((field) => (
          <span
            className="flex items-center gap-x-2 font-medium text-brand-green2 text-sm"
            key={field}
          >
            <div className="flex items-center gap-x-1 rounded-md bg-brand-white px-2 py-0.5">
              <span className="font-medium text-foreground">
                {review.ratingDetails[field]}
              </span>
              <Star className="h-4 w-4 [&>path]:fill-brand-yellow" />
            </div>
            {tCategories(field)}
          </span>
        ))}
      </div>

      {review.text && (
        <p className="mt-4 px-4 text-black text-sm md:px-6">{review.text}</p>
      )}

      {review.merchantAnswer && (
        <div className="mt-4 flex flex-col gap-y-3 bg-brand-grey-light px-4 py-4 md:px-6">
          <div className="flex flex-col">
            <span className="font-medium text-sm">
              {merchantName ?? t("merchantAnswer.author")}
            </span>
            {answerDate && (
              <span className="text-muted-foreground text-xs">
                {answerDate}
              </span>
            )}
          </div>
          <p className="text-black text-sm">{review.merchantAnswer}</p>
        </div>
      )}

      {review.deliveryRating && (
        <DeliveryReview
          delivery={review.deliveryRating}
          inverted={!!review.merchantAnswer}
        />
      )}
    </div>
  );
};
